import { inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { Organization, OrganizationContext, MyOrg } from 'src/app/common/models/user';
import { UtilsService, InMemoryCacheService, CoreService } from 'src/app/common';
import { AccessCacheService } from 'src/app/common/services/access-cache.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { OrganizationMembersService } from './organization-members.service';
import { OrganizationRolesService } from './organization-roles.service';
import { OrganizationProjectsService } from './organization-projects.service';
import { OrganizationLifecycleService } from './organization-lifecycle.service';
import { OrganizationBillingService } from './organization-billing.service';
import { OrganizationAuditService } from './organization-audit.service';
import { hasBusinessCardStandardFeatures, isBusinessCardStarterPlan, isBusinessCardBusinessPlan } from '../../digital-business-card/utils/business-card-plan-tier.util';

type OrganizationAddressPayload = {
        street: string;
        street1: string;
        city: string;
        state: string;
        zip: string;
        createdDate: string;
        modifiedDate: string;
};

@Injectable({
  providedIn: 'root'
})
export class OrganizationSettingsService {

    // Selected organization ID
    private _currentOrgId = signal<number | null>(null);
     // readonly signal (cannot be mutated outside)
    readonly currentOrgId = this._currentOrgId;

    private _organizations = signal<MyOrg[]>([]);
    // readonly signal (cannot be mutated outside)
    readonly organizations = () => this._organizations();

    private _currentOrg = signal<MyOrg | null>(null);
    // readonly signal (cannot be mutated outside)
    readonly currentOrg = () => this._currentOrg();

    private _showOrgDropdown = signal<boolean>(false);
    // readonly signal (cannot be mutated outside)
    readonly showOrgDropdown = () => this._showOrgDropdown();

    private _isSwitchingOrgs = signal<boolean>(false);
    // readonly signal (cannot be mutated outside)
    readonly isSwitchingOrgs = () => this._isSwitchingOrgs();

    private identityBaseUrl = environment.identityUrl;
    private mediaBaseUrl = environment.mediaServiceUrl;
    private core = inject(CoreService<{}>);
    private readonly lifecycle = inject(OrganizationLifecycleService);
    private cache = inject(InMemoryCacheService);
    private utils = inject(UtilsService);
    private accessCache = inject(AccessCacheService);
    private injector= inject(Injector);
    private orgMembersService = inject(OrganizationMembersService);
    private orgRolesService = inject(OrganizationRolesService);
    private orgProjectsService = inject(OrganizationProjectsService);
    private orgBillingService = inject(OrganizationBillingService);
    private orgAuditService = inject(OrganizationAuditService);

    private lastOrgId: number = null; // to track org changes and avoid unnecessary reloads
    private _dbcProductName = signal<string>('');
    public readonly businessCardPlanName = this._dbcProductName.asReadonly();

    public get dbcProductName(): string {
        return this._dbcProductName();
    }

    public get isStarterProduct(): boolean {
        return isBusinessCardStarterPlan(this.dbcProductName);
    }

    public get isStandardProduct(): boolean {
        return hasBusinessCardStandardFeatures(this.dbcProductName);
    }

    public get isBusinessProduct(): boolean {
        return isBusinessCardBusinessPlan(this.dbcProductName);
    }

    constructor() {
        if(!this.cache.auth?.user){
            this.utils.logger.info('No authenticated user found. Skipping organization load.');
            return;
        }

        this.loadOrganizationsAndSetCurrent();
    }

    public clearOrganizationState(): void {
        this.utils.logger.info('OrganizationSettingsService.clearOrganizationState()');
        this._organizations.set([]);
        this._currentOrg.set(null);
        this._currentOrgId.set(null);
        this._showOrgDropdown.set(false);
        this._isSwitchingOrgs.set(false);
        this.lastOrgId = null;
    }

    public async loadOrganizationsAndSetCurrent(): Promise<void> {
        await this.loadUserOrganizations();
        const currentOrgId = this.cache.organization?.id ?? null;
        if (!currentOrgId) {
            this.initCurrentOrgId();
            return;
        }

        const foundOrg = this.organizations().find(o => o.id === currentOrgId);
        if (!foundOrg) {
            this.utils.logger.warn('Cached organization not found in the loaded list of organizations.');
            this.initCurrentOrgId();
            return;
        }

        this.utils.logger.info('Organization context found in cache. Setting current organization to:', foundOrg);
        this._currentOrg.set(foundOrg);
        this._currentOrgId.set(foundOrg.id);
    }

    public async getOrgFromCloud(): Promise<Organization | null> {
        return await this.getOrgById(this.currentOrgId()!);
    }


    // Show or hide organization dropdown in topbar
    public setShowOrgDropdown(value: boolean = true): void {
        this._showOrgDropdown.set(value);
    }

    public async uploadLogoUrl(file: File): Promise<any> {
        try {
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'organization/general/logo-url');

            const uploadUrl = `${this.mediaBaseUrl}/Upload/streaming`;
            this.utils.logger.info('uploadLogoUrl() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('uploadLogoUrl upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            return response;
        } catch (error) {
            // return this.handleApiError(error, 'Failed to upload logo');
        }
    }

    public async deletePhotoUrl(path: string): Promise<any> {
        try {
            this.utils.logger.info('deleteMediaFile() Deleting file:', path);

            const deleteUrl = `${this.mediaBaseUrl}/Upload?path=${path}`;

            const response = await firstValueFrom(this.core.delete(deleteUrl));

            if (response?.deleted) {
                this.utils.logger.info('File deleted successfully:', response);
            } else {
                this.utils.logger.warn('File deletion response did not confirm deletion:', response);
            }

            return {
                success: !!response?.deleted
            };
        } catch (error) {
            // return this.handleApiError(error, 'Failed to delete file');
        }
    }

    public async checkOrgSlugAvailability(slug: string): Promise<boolean> {
        this.utils.logger.info('checkOrgSlugAvailability() Checking slug availability:', slug);

        if (!slug || !slug.trim()) {
            this.utils.logger.warn('checkOrgSlugAvailability() No slug provided');
            return false;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/slug-availability?slug=${encodeURIComponent(slug.trim())}`;
            this.utils.logger.info('checkOrgSlugAvailability() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            return Boolean((data as { available: boolean }).available);

        } catch (error) {
            this.utils.logger.error('checkOrgSlugAvailability() API error details:', error);
            return false;
        } finally {
            this.utils.logger.info('checkOrgSlugAvailability() Completed slug availability check for:', slug);
        }
    }

    /**
     * Updates the current organization context.
     * Triggered when the organization switcher changes.
     *
     * Flow:
     * 1. Attempt to resolve the organization from the local cache.
     * 2. If not found, fetch the organization from the API.
     * 3. Update reactive state and persist the selected org ID.
     */
    async setCurrentOrg(org: MyOrg): Promise<void> {

        this.utils.logger.info('setCurrentOrg() Attempting to set current organization to:', org);

        if (!org?.id || this.lastOrgId === org.id) {
            if (this.lastOrgId === org?.id) {
                this.utils.logger.info(
                    'setCurrentOrg() Organization is the same as current. No action taken for orgId:',
                    org.id
                );
            }
            return;
        }

        if (!this.cache.stripe) {
            this.utils.logger.info('Initializing Stripe...');
            this.cache.stripeInit();
        }

        this._isSwitchingOrgs.set(true);
        this.cache.loading = true;
        try {

            //set org context in the backend so that API calls will be made in the context of the new org
            const orgContext = await this.setOrganizationContext(org.id);
            if (orgContext) {
                this.utils.logger.info('setCurrentOrg() Successfully set organization context in backend', orgContext);
            } else {
                this.utils.logger.error('setCurrentOrg() Failed to set organization context in backend. Aborting org switch.');
                return;
            }

            this._currentOrg.set(org);
            this._currentOrgId.set(org.id);

            // Clear access caches on org switch
            this.accessCache.clearOnOrgSwitch();
            this.orgMembersService.clearCache();
            this.orgProjectsService.clearCache();

            // Load org-scoped data in parallel
            await Promise.all([
                this.orgMembersService.loadMembersIfNeeded(org.id),
                this.orgRolesService.loadRolesPolicyIfNeeded(org.id)
            ]);

            // Load only org-switch dependencies that affect navigation and feature visibility.
            await this.orgBillingService.getBillingProducts();
            await this.refreshBusinessCardPlanName();

            this.lastOrgId = org.id;

        } finally {
            // Always reset UI loading state
            setTimeout(() => {
                this._isSwitchingOrgs.set(false);
                this.cache.loading = false;
            }, 300); // 300ms feels snappier than 2s
        }
    }

    /**
     * Hard delete the organization permanently.
     * API: POST /organization/{orgId}/hard-delete
     */
    public async closeOrganization(orgId: number): Promise<boolean> {
        try {
            await this.lifecycle.submit(orgId, { operationId: crypto.randomUUID(), action: 'close-soft', reason: 'Owner requested organization closure' });
            return true;
        } catch (error) {
            this.utils.handleApiError(error, 'Unable to schedule organization closure.');
            return false;
        }
    }

    public async hardDeleteOrganization(orgId: number): Promise<boolean> {
        return this.closeOrganization(orgId);
    }

    /**
     * Update organization details
     * API: PUT /Organization
     */
    async updateOrganization(payload: Organization): Promise<Organization | null> {
        this.utils.logger.info('updateOrganization() Updating organization:', payload?.id);
        this.utils.logger.info('updateOrganization() Payload details:', payload);

        if (!payload || payload.id == null) {
            this.utils.logger.warn('updateOrganization() Invalid payload or missing org id');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization`;
            this.utils.logger.info('updateOrganization() API URL:', url);

            const data = await firstValueFrom(this.core.put(url, payload));
            this.utils.logger.info('updateOrganization() API response:', data);

            // If successfully updated org, also update the current org context in cache to reflect changes across the app
            // Better if update "`${this.identityBaseUrl}/Organization`;" org return the updated org object, so we don't have to make an additional API call to get the updated org details. Assuming it does return the updated org:
             if (data) {
                this.utils.logger.info('updateOrganization() Successfully updated organization. Updating cache with new org data.');
                const orgContext = await this.setOrganizationContext(payload.id!);
                return orgContext as Organization;
            }
            return data as null;

        } catch (error) {
            this.utils.logger.error('updateOrganization() API error details:', error);
            return null;
        } finally {
            this.utils.logger.info('updateOrganization() Completed update for org:', payload?.id);
        }
    }

    public async updateOrganizationAddress(organizationId: number, payload: OrganizationAddressPayload): Promise<boolean> {
        this.utils.logger.info('updateOrganizationAddress() Updating address for org:', organizationId);
        this.utils.logger.info('updateOrganizationAddress() Payload details:', payload);

        if (!organizationId) {
            this.utils.logger.warn('updateOrganizationAddress() Invalid organization id');
            return false;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${organizationId}/address`;
            this.utils.logger.info('updateOrganizationAddress() API URL:', url);
            await firstValueFrom(this.core.bulkPut(url, payload));

            if (this.cache.organization?.id === organizationId) {
                this.cache.organization = {
                    ...this.cache.organization,
                    address: {
                        ...this.cache.organization.address,
                        ...payload,
                        createdDate: new Date(payload.createdDate),
                        modifiedDate: new Date(payload.modifiedDate)
                    }
                };
            }

            return true;
        } catch (error) {
            this.utils.logger.error('updateOrganizationAddress() API error details:', error);
            return false;
        } finally {
            this.utils.logger.info('updateOrganizationAddress() Completed update for org:', organizationId);
        }
    }

    public navigateTo(path: string): void {
        runInInjectionContext(this.injector, () => {
        const router = inject(Router);
            router.navigate([path]);
        });
    }

    /**
     * Restore in-memory org state on page load by fetching the current context
     * from the server via GET. Does NOT use the PUT me/context endpoint — that
     * is reserved for actual org switches.
     */
    public async initCurrentOrgId(): Promise<void> {
        const orgContext = await this.getOrganizationContext();

        const currentOrgId = orgContext?.organization?.id ?? this.cache.organization?.id ?? null;
        const organizations = this.organizations() ?? [];
        const org = organizations.find(o => o.id === currentOrgId) ?? null;

        if (orgContext?.organization) {
            this.cache.organization = orgContext.organization;
            if (this.cache.user) {
                this.cache.user.currentOrganizationId = orgContext.organization.id;
            }
        }

        if (org) {
            this._currentOrg.set(org);
            this._currentOrgId.set(org.id);
            this.lastOrgId = org.id;
            await this.orgBillingService.getBillingProducts();
            await this.refreshBusinessCardPlanName();
            return;
        }

        if (currentOrgId !== null) {
            this._currentOrgId.set(currentOrgId);
            await this.orgBillingService.getBillingProducts();
            await this.refreshBusinessCardPlanName();
        }
    }

    public async getOrganizationContext(): Promise<OrganizationContext | null> {
        this.utils.logger.info('getOrganizationContext() Fetching current org context from API');
        try {
            const url = `${this.identityBaseUrl}/me/context`;
            this.utils.logger.info('getOrganizationContext() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return data as OrganizationContext;
        } catch (error) {
            this.utils.logger.error('getOrganizationContext() API error details:', error);
            return null;
        } finally {
            this.utils.logger.info('getOrganizationContext() Completed');
        }
    }

    public async setOrganizationContext(currentOrganizationId: number): Promise<OrganizationContext> {
        this.utils.logger.info('setOrganizationContext() Fetching organizations from API');
        try {
            const url = `${this.identityBaseUrl}/me/context`;
            this.utils.logger.info('setOrganizationContext() API URL:', url);
            const data = await firstValueFrom(this.core.bulkPut(url, { currentOrganizationId }));
            this.cache.organization = data.organization; // Update cache with new org context
            this.cache.user.currentOrganizationId = data.organization?.id; // Ensure user context is also updated with new org id
            return data as OrganizationContext;
        } catch (error) {
            this.utils.logger.error('setOrganizationContext() API error details:', error);
        } finally {
            this.utils.logger.info('setOrganizationContext() Completed fetching organizations');
        }
        return null;
    }

    public async getOrgMembershipByOrganizationAndUserId(organizationId: number, userId: string): Promise<any | null> {
        this.utils.logger.info('getOrgMembershipByOrganizationAndUserId() Fetching membership for org/user:', organizationId, userId);

        if (!organizationId || !userId) {
            this.utils.logger.warn('getOrgMembershipByOrganizationAndUserId() Missing organizationId or userId');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/OrgMemberships/organization/${organizationId}/user/${userId}`;
            this.utils.logger.info('getOrgMembershipByOrganizationAndUserId() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return data as any;
        } catch (error) {
            this.utils.logger.error('getOrgMembershipByOrganizationAndUserId() API error details:', error);
            return null;
        } finally {
            this.utils.logger.info('getOrgMembershipByOrganizationAndUserId() Completed fetching membership for org/user:', organizationId, userId);
        }
    }

    public async loadUserOrganizations(): Promise<void> {
        this.utils.logger.info('loadUserOrganizations() Fetching organizations from API');
        try {
            const url = `${this.identityBaseUrl}/me/organizations`;
            this.utils.logger.info('loadUserOrganizations() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            const orgs = Array.isArray(data) ? data as MyOrg[] : [];
            this._organizations.set(orgs);
            this.utils.logger.info('loadUserOrganizations() Fetched organizations:', orgs);
        } catch (error) {
            this.utils.logger.error('loadUserOrganizations() API error details:', error);
            this._organizations.set([]);
        } finally {
            this.utils.logger.info('loadUserOrganizations() Completed fetching organizations');
        }
    }

    private async getOrgById(orgId: number): Promise<Organization | null> {
        this.utils.logger.info('getOrgById() Fetching Organization for orgId:', orgId);

        if (orgId == null) {
            this.utils.logger.warn('getOrgById() No orgId provided');
            return null;
        }
        this.cache.loading = true;
        try {
            const url = `${this.identityBaseUrl}/Organization/${orgId}`;
            this.utils.logger.info('getOrgById() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return data as Organization;
        } catch (error) {
            this.utils.logger.error('getOrgById() API error details:', error);
            return null;
        } finally {
            this.utils.logger.info('getOrgById() Completed fetching organization for orgId:', orgId);
            this.cache.loading = false;
        }
    }

    private async refreshBusinessCardPlanName(): Promise<void> {
        const planName = await this.cache.getBusinessCardPlanName();
        this._dbcProductName.set(planName ?? '');
    }
}
